import React, { useCallback, useEffect, useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import Select from "react-select";
import { useDispatch } from "react-redux";
import {
  CustomDropdownContainer,
  FormFieldLabel,
  PageHeader,
  StatusBadge,
} from "CommonComponent";
import { hideCommonLoader, showCommonLoader } from "actions/commonActions";
import { useObjectViewedId } from "hooks/useObjectViewedId";
import {
  ENCRYPTION_OPTIONS,
  fetchEmailSmtpSettings,
  getErrorMessage,
  testEmailSmtpSettings,
  updateEmailSmtpSettings,
} from "../emailSettingsApi";
import "../EmailSettings.css";

const notify = (message, type = "general_error") => ({
  type: "SHOW_SNACKBAR",
  snackbar_notification: message,
  snackbar_notification_type: type,
});

const selectMenuProps = {
  menuPortalTarget: typeof document !== "undefined" ? document.body : null,
  menuPosition: "fixed",
  styles: {
    menuPortal: (base) => ({ ...base, zIndex: 10050 }),
  },
};

const emptyForm = {
  mailer: "smtp",
  host: "",
  port: "",
  encryption: "",
  username: "",
  from_address: "",
  from_name: "",
  is_enabled: false,
  local_redirect_enabled: true,
  local_redirect_email: "shahzad.iqbal@premiernx.com",
};

/** The API never returns the stored password; it only reports whether one exists. */
const toForm = (data) => ({
  mailer: data?.mailer || "smtp",
  host: data?.host || "",
  port: data?.port ?? "",
  encryption: data?.encryption || "",
  username: data?.username || "",
  from_address: data?.from_address || "",
  from_name: data?.from_name || "",
  is_enabled: !!data?.is_enabled,
  local_redirect_enabled: data?.local_redirect_enabled !== false,
  local_redirect_email:
    data?.local_redirect_email || "shahzad.iqbal@premiernx.com",
});

const EmailSmtpSettings = () => {
  const dispatch = useDispatch();
  const objectViewedId = useObjectViewedId();
  const [form, setForm] = useState(emptyForm);
  const [passwordSet, setPasswordSet] = useState(false);
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testing, setTesting] = useState(false);
  const [appEnv, setAppEnv] = useState("local");
  const [localRedirectActive, setLocalRedirectActive] = useState(false);

  const load = useCallback(async () => {
    dispatch(showCommonLoader());
    try {
      const data = await fetchEmailSmtpSettings(objectViewedId);
      setForm(toForm(data));
      setPasswordSet(!!data?.password_set);
      setPassword("");
      setAppEnv(data?.app_env || "local");
      setLocalRedirectActive(!!data?.local_redirect_active);
    } catch (error) {
      dispatch(notify(getErrorMessage(error)));
    } finally {
      dispatch(hideCommonLoader());
    }
  }, [dispatch, objectViewedId]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    setSaving(true);
    dispatch(showCommonLoader());
    try {
      const payload = {
        mailer: form.mailer || "smtp",
        host: form.host || null,
        port: form.port === "" ? null : Number(form.port),
        encryption: form.encryption || null,
        username: form.username || null,
        from_address: form.from_address || null,
        from_name: form.from_name || null,
        is_enabled: !!form.is_enabled,
        local_redirect_enabled: !!form.local_redirect_enabled,
        local_redirect_email: form.local_redirect_email || null,
      };
      if (password.trim() !== "") {
        payload.password = password;
      }
      const data = await updateEmailSmtpSettings(payload, objectViewedId);
      setForm(toForm(data));
      setPasswordSet(!!data?.password_set);
      setPassword("");
      setAppEnv(data?.app_env || appEnv);
      setLocalRedirectActive(!!data?.local_redirect_active);
      dispatch(notify("SMTP configuration saved.", "success"));
    } catch (error) {
      dispatch(notify(getErrorMessage(error)));
    } finally {
      setSaving(false);
      dispatch(hideCommonLoader());
    }
  };

  const sendTest = async () => {
    const to = testEmail.trim();
    if (!to) return;
    setTesting(true);
    dispatch(showCommonLoader());
    try {
      const result = await testEmailSmtpSettings({ to_email: to }, objectViewedId);
      dispatch(notify(result?.message || `Test email sent to ${to}.`, "success"));
    } catch (error) {
      dispatch(notify(getErrorMessage(error)));
    } finally {
      setTesting(false);
      dispatch(hideCommonLoader());
    }
  };

  return (
    <div className="email-settings-page">
      <PageHeader title="SMTP Configuration">
        <Button type="button" className="n-u-btn" disabled={saving} onClick={save}>
          Save
        </Button>
      </PageHeader>

      <div className="main-content-div page-card">
        <div className="email-settings-section-title">Connection</div>
        <Row className="g-3 mb-2">
          <Col md={3}>
            <FormFieldLabel>Mail Driver</FormFieldLabel>
            <Form.Control
              value={form.mailer}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, mailer: e.target.value }))
              }
              placeholder="smtp"
            />
          </Col>
          <Col md={4}>
            <FormFieldLabel>Host</FormFieldLabel>
            <Form.Control
              value={form.host}
              onChange={(e) => setForm((prev) => ({ ...prev, host: e.target.value }))}
              placeholder="smtp.example.com"
            />
          </Col>
          <Col md={2}>
            <FormFieldLabel>Port</FormFieldLabel>
            <Form.Control
              type="number"
              min={1}
              max={65535}
              value={form.port}
              onChange={(e) => setForm((prev) => ({ ...prev, port: e.target.value }))}
              placeholder="587"
            />
          </Col>
          <Col md={3}>
            <FormFieldLabel>Encryption</FormFieldLabel>
            <Select
              className="multi-select"
              classNamePrefix="react-select"
              options={ENCRYPTION_OPTIONS}
              value={
                ENCRYPTION_OPTIONS.find((o) => o.value === (form.encryption || "")) ||
                null
              }
              onChange={(opt) =>
                setForm((prev) => ({ ...prev, encryption: opt?.value || "" }))
              }
              components={{ ValueContainer: CustomDropdownContainer }}
              {...selectMenuProps}
            />
          </Col>
        </Row>

        <div className="email-settings-section-title">Credentials</div>
        <Row className="g-3 mb-2">
          <Col md={4}>
            <FormFieldLabel>Username</FormFieldLabel>
            <Form.Control
              value={form.username}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, username: e.target.value }))
              }
              autoComplete="off"
            />
          </Col>
          <Col md={4}>
            <FormFieldLabel>Password</FormFieldLabel>
            <Form.Control
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Leave blank to keep existing"
              autoComplete="new-password"
            />
          </Col>
          <Col md={4} className="d-flex align-items-end">
            {passwordSet ? (
              <StatusBadge label="Password Set" variant="active" />
            ) : (
              <StatusBadge label="No Password Stored" variant="inactive" />
            )}
          </Col>
        </Row>

        <div className="email-settings-section-title">Sender</div>
        <Row className="g-3 mb-2">
          <Col md={4}>
            <FormFieldLabel>From Email</FormFieldLabel>
            <Form.Control
              type="email"
              value={form.from_address}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, from_address: e.target.value }))
              }
              placeholder="no-reply@example.com"
            />
          </Col>
          <Col md={4}>
            <FormFieldLabel>From Name</FormFieldLabel>
            <Form.Control
              value={form.from_name}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, from_name: e.target.value }))
              }
              placeholder="Naxceed"
            />
          </Col>
          <Col md={4} className="d-flex align-items-end">
            <Form.Check
              type="switch"
              id="smtp-enabled"
              label="Enable SMTP"
              checked={!!form.is_enabled}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, is_enabled: e.target.checked }))
              }
            />
          </Col>
        </Row>

        <div className="email-settings-section-title">Email Redirect</div>
        <p className="email-settings-hint">
          When enabled, PAF emails (generation, scoring, reminders, manager
          review) are redirected to the address below instead of real
          supervisors/managers. Works in <strong>any</strong> environment
          (local, staging, production). Disable this before go-live if you want
          real recipients to receive mail.
        </p>
        <Row className="g-3 mb-2">
          <Col md={3}>
            <FormFieldLabel>Current Environment</FormFieldLabel>
            <div className="email-settings-readonly">{appEnv || "—"}</div>
          </Col>
          <Col md={3} className="d-flex align-items-end">
            {localRedirectActive ? (
              <StatusBadge label="Redirect Active" variant="active" />
            ) : (
              <StatusBadge label="Redirect Inactive" variant="inactive" />
            )}
          </Col>
          <Col md={6} className="d-flex align-items-end">
            <Form.Check
              type="switch"
              id="local-redirect-enabled"
              label="Enable email redirect"
              checked={!!form.local_redirect_enabled}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  local_redirect_enabled: e.target.checked,
                }))
              }
            />
          </Col>
          <Col md={6}>
            <FormFieldLabel>Redirect Email</FormFieldLabel>
            <Form.Control
              type="email"
              value={form.local_redirect_email}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  local_redirect_email: e.target.value,
                }))
              }
              placeholder="you@example.com"
              disabled={!form.local_redirect_enabled}
            />
          </Col>
        </Row>

        <div className="email-settings-section-title">Test Email</div>
        <p className="email-settings-hint">
          Sends a test message using the saved configuration. Save your changes
          first. Results (sent or failed) appear under Mail Logs as “SMTP Test
          Email”.
        </p>
        <Row className="g-3">
          <Col md={4}>
            <FormFieldLabel>Recipient</FormFieldLabel>
            <Form.Control
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="you@example.com"
            />
          </Col>
          <Col md={4} className="d-flex align-items-end">
            <Button
              type="button"
              className="n-u-btn"
              disabled={testing || !testEmail.trim()}
              onClick={sendTest}
            >
              Send Test
            </Button>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default EmailSmtpSettings;
